import { API_BASE_URL } from "../consts/apiRoutes.consts"
import * as requester from "../services/base/api.service"
import { strAfter, strBefore } from "./strings.utils"

/**
 * @typedef {{
 *  internal: boolean;
 *  message?: string;
 * } | null} ErrorType
 */

/**
 * @template T
 *
 * @typedef {{
 *  success: boolean;
 *  error?: ErrorType;
 *  body?: { data: T };
 *  headers?: Record<string, any>;
 * }} ResponseType
 */

const BACKEND = {
  BASE_URL: API_BASE_URL,
  AUTH: {
    HEADER: "authorization",
  },
}

/**
 * @param {unknown} error
 * @returns
 */
const errorHandler = (error) => {
  console.log({ error })

  return { success: false, error: { internal: true } }
}

/**
 * @template T
 * @param {string} route
 * @param {Record<string, any>} config
 * @returns {Promise<ResponseType<T>>}
 */
async function getRequest(route, config = {}) {
  try {
    const routePath = strBefore(route, "?")
    const params = strAfter(route, "?")
    const response = await requester.get(
      `${BACKEND.BASE_URL}${routePath}`,
      params,
      config
    )
    let data = null

    try {
      data = await response.json()
    } catch (error) {
      data = response.body
      console.log({
        data,
      })
    }

    const { headers } = response

    return { success: true, error: null, body: data, headers }
  } catch (error) {
    return errorHandler(error)
  }
}

/**
 * @template T
 * @param {string} route
 * @param {Record<string, any>} body
 * @param {Record<string, any>} config
 * @returns {Promise<ResponseType<T>>}
 */
async function postRequest(route, body = {}, config = {}) {
  try {
    const response = await requester.post(
      `${BACKEND.BASE_URL}${route}`,
      body,
      config
    )

    let data = null
    const clone = response.clone()
    try {
      data = await response.json()
    } catch (error) {
      data = await clone.blob()
    }
    const { headers } = response

    return { success: true, body: data, headers }
  } catch (error) {
    return errorHandler(error)
  }
}

/**
 * @template T
 * @param {string} route
 * @param {Record<string, any>} body
 * @param {Record<string, any>} config
 * @returns {Promise<ResponseType<T>>}
 */
async function putRequest(route, body = {}, config = {}) {
  try {
    const response = await requester.put(
      `${BACKEND.BASE_URL}${route}`,
      body,
      config
    )

    const data = await response.json()
    const { headers } = response

    return { success: true, body: data, headers }
  } catch (error) {
    return errorHandler(error)
  }
}

/**
 * @template T
 * @param {string} route
 * @param {Record<string, any>} config
 * @returns {Promise<ResponseType<T>>}
 */
async function deleteRequest(route, config = {}) {
  try {
    const request = await requester.del(`${BACKEND.BASE_URL}${route}`, config)
    let data

    try {
      data = await request.json()
    } catch (error) {
      if (!request.ok) throw new Error("Failed to parse response as JSON")
    }

    const { headers } = request

    return { success: true, body: data, headers }
  } catch (error) {
    return errorHandler(error)
  }
}

export { getRequest, postRequest, putRequest, deleteRequest }
